/* eslint-disable unicorn/no-null, @typescript-eslint/no-unnecessary-condition */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import type {
    TImportProjectVersionParams,
    TProjectVersion,
    TUpdateProjectVersionDto
} from '@newo/api/gateway';
import { ProjectVersionStatus } from '@newo/api/gateway';
import { FILE_QUERY_KEYS } from './Files.queries';
import useDesignerFilesStore from './DesignerFiles.store';
import {
    ARCHIVE_ERROR_NOTIFICATION_DURATION,
    extractServerErrorMessage,
    isRequestTimeout,
    uploadArchiveToSignedUrl
} from '@/utils/api/importArchive';
import { triggerDirectDownload } from '@/utils';
import {
    finishProgressNotification,
    showProgressNotification
} from '@/utils/ui';
import { designerV2API } from '@/services/designer/designerV2Client';
import { gcsClient } from '@/utils/api/gcsClient.service';

export type TOptimisticProjectVersion = TProjectVersion & {
    isOptimistic?: boolean;
};

const buildOptimisticVersion = (
    tempId: string,
    projectIdn: string,
    forkedFromId: string | null
): TOptimisticProjectVersion => {
    const now = new Date().toISOString();
    return {
        id: tempId,
        project_idn: projectIdn,
        version_prefix: '',
        version_increment: 0,
        status: ProjectVersionStatus.Pending,
        s3_prefix: '',
        created_by: '',
        forked_project_version_id: forkedFromId,
        validated_at: null,
        published_at: null,
        latest_error_message: null,
        created_at: now,
        updated_at: now,
        isOptimistic: true
    };
};

const PROJECT_VERSION_QUERY_KEYS = {
    listVersions: (projectIdn: string) =>
        ['project-versions', projectIdn] as const,
    listAllProjectVersions: () => ['project-versions', 'all'] as const
};

interface CreateVersionVariables {
    projectIdn: string;
    sourceProjectVersionId?: string;
}

export const useProjectVersionsQuery = (projectIdn?: string) => {
    return useQuery<TOptimisticProjectVersion[]>({
        queryKey: PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn ?? ''),
        queryFn: async () =>
            designerV2API.listProjectVersions({
                project_idn: projectIdn
            }),
        enabled: Boolean(projectIdn)
    });
};

export const useAllProjectVersionsQuery = (enabled = true) =>
    useQuery({
        queryKey: PROJECT_VERSION_QUERY_KEYS.listAllProjectVersions(),
        queryFn: async () => designerV2API.listProjectVersions({}),
        enabled
    });

export const useCreateVersionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['version', 'create'],
        mutationFn: async ({
            projectIdn,
            sourceProjectVersionId
        }: CreateVersionVariables) =>
            designerV2API.createProjectVersion({
                project_idn: projectIdn,
                ...(sourceProjectVersionId
                    ? { source_project_version_id: sourceProjectVersionId }
                    : {})
            }),
        onMutate: async ({ projectIdn, sourceProjectVersionId }) => {
            const queryKey =
                PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn);
            await queryClient.cancelQueries({ queryKey });
            const previous =
                queryClient.getQueryData<TProjectVersion[]>(queryKey);

            const tempId = crypto.randomUUID();
            const isFork = Boolean(sourceProjectVersionId);
            const placeholder = buildOptimisticVersion(
                tempId,
                projectIdn,
                sourceProjectVersionId ?? null
            );

            queryClient.setQueryData<TProjectVersion[]>(queryKey, (old) => [
                placeholder,
                ...(old ?? [])
            ]);
            const notificationId = `${isFork ? 'fork' : 'create'}-version-${tempId}`;
            showProgressNotification(
                notificationId,
                isFork ? 'Forking version' : 'Creating version',
                isFork
                    ? 'Creating a fork of the selected version…'
                    : 'Allocating a new project version…'
            );
            return { previous, notificationId, isFork };
        },
        onSuccess: (_result, _variables, context) => {
            if (context?.notificationId) {
                finishProgressNotification(
                    context.notificationId,
                    'green',
                    context.isFork ? 'Version forked' : 'Version created'
                );
            }
        },
        onError: (_error, { projectIdn }, context) => {
            if (context?.previous) {
                queryClient.setQueryData(
                    PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn),
                    context.previous
                );
            }
            if (context?.notificationId) {
                finishProgressNotification(
                    context.notificationId,
                    'red',
                    context.isFork
                        ? 'Could not fork version'
                        : 'Could not create version',
                    'Please try again.'
                );
            }
        },
        onSettled: (_data, _error, { projectIdn }) => {
            void queryClient.invalidateQueries({
                queryKey: PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn)
            });
        }
    });
};

export const useUpdateVersionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            versionId,
            data
        }: {
            versionId: string;
            projectIdn: string;
            data: TUpdateProjectVersionDto;
        }) => designerV2API.updateProjectVersion(versionId, data),
        onSuccess: (_result, { projectIdn }) => {
            void queryClient.invalidateQueries({
                queryKey: PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn)
            });
        },
        onError: () => {
            notifications.show({
                color: 'red',
                message: 'Could not update version. Please try again.'
            });
        }
    });
};

interface DeleteVersionVariables {
    versionId: string;
    projectIdn: string;
}

export const useDeleteVersionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ versionId }: DeleteVersionVariables) =>
            designerV2API.deleteProjectVersion(versionId),
        onMutate: ({ versionId }) => {
            const notificationId = `delete-version-${versionId}`;
            showProgressNotification(
                notificationId,
                'Deleting version',
                'Removing version from the project…'
            );
            return { notificationId };
        },
        onSuccess: (_result, { versionId, projectIdn }, ctx) => {
            queryClient.setQueryData<TProjectVersion[]>(
                PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn),
                (versions) =>
                    versions?.filter((version) => version.id !== versionId)
            );
            queryClient.removeQueries({
                queryKey: FILE_QUERY_KEYS.fileList(versionId)
            });
            const store = useDesignerFilesStore.getState();
            if (store.selectedVersionId === versionId) {
                store.clearOpenFiles();
                gcsClient.reset();
                store.setSelectedVersionId(null);
            }
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    'Version deleted'
                );
            }
        },
        onError: (_error, _variables, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    'Could not delete version',
                    'Please try again.'
                );
            }
        },
        onSettled: (_data, _error, { projectIdn }) => {
            void queryClient.invalidateQueries({
                queryKey: PROJECT_VERSION_QUERY_KEYS.listVersions(projectIdn)
            });
        }
    });
};

export const usePublishVersionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            versionId
        }: {
            versionId: string;
            projectIdn: string;
        }) => designerV2API.publishProjectVersion(versionId),
        onSuccess: (_result, { projectIdn: publishedProjectIdn }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    PROJECT_VERSION_QUERY_KEYS.listVersions(publishedProjectIdn)
            });
            notifications.show({
                color: 'green',
                message: 'Version published.'
            });
        },
        onError: () => {
            notifications.show({
                color: 'red',
                message: 'Could not publish version. Please try again.'
            });
        }
    });
};

const buildExportFilename = (version: TProjectVersion): string => {
    const safeIdn = version.project_idn.replaceAll(/[^\w.-]/g, '_');
    const safePrefix =
        version.version_prefix.replaceAll(/[^\w.-]/g, '_') || 'version';
    return `${safeIdn}-${safePrefix}.${version.version_increment}.zip`;
};

export const useExportVersionMutation = () => {
    return useMutation({
        mutationKey: ['version', 'export'],
        mutationFn: async (version: TProjectVersion) => {
            const urlDto = await designerV2API.createProjectVersionExportUrl(
                version.id
            );
            return { downloadUrl: urlDto.download_url, version };
        },
        onMutate: (version) => {
            const notificationId = `export-version-${version.id}`;
            showProgressNotification(
                notificationId,
                'Exporting version',
                'Preparing archive…'
            );
            return { notificationId };
        },
        onSuccess: ({ downloadUrl, version }, _vars, ctx) => {
            triggerDirectDownload(downloadUrl, buildExportFilename(version));
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    'Version exported'
                );
            }
        },
        onError: (_error, _vars, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    'Could not export version',
                    'Please try again.'
                );
            }
        }
    });
};

export const useImportVersionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['version', 'import'],
        mutationFn: async ({
            archive,
            params
        }: {
            archive: File;
            params: TImportProjectVersionParams;
        }) => {
            const urlDto = await designerV2API.createProjectVersionImportUrl();
            await uploadArchiveToSignedUrl(
                archive,
                urlDto.upload_url,
                urlDto.method,
                urlDto.headers
            );
            return designerV2API.completeProjectVersionImport({
                project_idn: params.project_idn,
                object_name: urlDto.object_name,
                version_prefix: params.version_prefix,
                publish_after_import: params.publish_after_import
            });
        },
        onMutate: ({ archive }) => {
            const notificationId = `import-version-${archive.name}-${archive.size}`;
            showProgressNotification(
                notificationId,
                'Importing version',
                `Uploading ${archive.name}…`
            );
            return { notificationId };
        },
        onSuccess: (_result, _vars, ctx) => {
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'green',
                    'Version imported'
                );
            }
        },
        onError: (error, _vars, ctx) => {
            const detail = extractServerErrorMessage(error);

            const message =
                detail ??
                (isRequestTimeout(error)
                    ? 'Upload timed out. Try a smaller archive or check your connection.'
                    : 'Archive was rejected. Please try a different file.');
            if (ctx?.notificationId) {
                finishProgressNotification(
                    ctx.notificationId,
                    'red',
                    'Could not import version',
                    message,
                    ARCHIVE_ERROR_NOTIFICATION_DURATION
                );
            }
        },
        onSettled: (_data, _error, { params }) => {
            void queryClient.invalidateQueries({
                queryKey: PROJECT_VERSION_QUERY_KEYS.listVersions(
                    params.project_idn
                )
            });
        }
    });
};
